import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Backdrop, CircularProgress } from '@mui/material';

//application services
import SpinnerService, { SpinnerStatusType } from '../../services/SpinnerService';
import SystemService from '../../services/SystemService';
import SizingToolService, { TabState, dashboardTabWorkflowId } from '../../services/SizingToolService';

//application components
import CleanDash from '../Dashboard/CleanDash';
import DynamicTab from './DynamicTab';
import DynamicPanel from './DynamicPanel';

//styles
import styles from './DynamicTabSystem.module.scss';

import { launchSizingTool } from '../../utils/Utils';

export interface ITabDetail {
  title: string;
  workflowId: string;
  usableComponent: JSX.Element | null;
}
export interface ITabSystemData {
  details: ITabDetail[];
  activeTabWorkflowId: string;
}

export default function DynamicTabSystem() {
  const { t } = useTranslation();
  const { systemId } = useParams();
  const { systemName } = useParams();

  const [tabSystemDataState, setTabSystemDataState] = useState<ITabSystemData>({
    details: [],
    activeTabWorkflowId: dashboardTabWorkflowId,
  });

  const [tabState, setTabState] = useState<TabState>({
    workflowId: '',
    isOpen: false,
  });

  const [showSpinner, setShowSpinner] = useState<SpinnerStatusType>({
    pendingApiResponses: 0,
    isIframeLoaded: true,
  });

  function makeTabActive(workflowId: string) {
    const modified = { ...tabSystemDataState };
    modified.activeTabWorkflowId = workflowId;
    SizingToolService.assignTabSystemDataState(modified);
  }

  function removeTab(workflowId: string) {
    SizingToolService.closeTab(workflowId, false);
  }

  useEffect(() => {
    if (!tabState.isOpen) {
      removeTab(tabState.workflowId);
    }
  }, [tabState]);

  useEffect(() => {
    if (systemId) {
      const main = async () => {
        await SystemService.fetchSystemById(systemId);
      };
      main();
    } else if (systemName) {
      SystemService.fetchFakeSystem(systemName);
    }
  }, [systemId, systemName]);

  useEffect(() => {
    SpinnerService.getSpinnerStatus(setShowSpinner);

    SizingToolService.getTabState(setTabState);
    SizingToolService.getTabSystemDataState(setTabSystemDataState);

    const tsData: ITabSystemData = {
      details: [],
      activeTabWorkflowId: dashboardTabWorkflowId,
    };

    const tab1: ITabDetail = {
      title: t('Dashboard'),
      workflowId: dashboardTabWorkflowId,
      usableComponent: systemName || systemId ? <CleanDash handleLaunchSizingTool={launchSizingTool} tabState={tabState} /> : null,
    };
    tsData.details.push(tab1);
    SizingToolService.assignTabSystemDataState(tsData);
  }, []);

  const tabs = tabSystemDataState.details.map((tab) => (
    <DynamicTab
      key={tab.workflowId}
      id={tab.workflowId}
      isActive={tabSystemDataState.activeTabWorkflowId === tab.workflowId}
      tabTitle={tab.title}
      handleClick={makeTabActive}
      handleClose={removeTab}
    />
  ));

  const panels = tabSystemDataState.details.map((tab) => {
    return (
      <DynamicPanel
        key={tab.workflowId}
        title={tab.title}
        isActive={!(tabSystemDataState.activeTabWorkflowId === tab.workflowId)}
        goodComponent={tab.usableComponent}
      />
    );
  });

  return (
    <div className={styles['dynamic-tab-system']}>
      <div id="dynaTabStrip" className={styles['dynamic-system-header']}>
        <div className={styles['tab-set']}>{tabs}</div>
      </div>
      <div className={styles['dynamic-system-body']}>
        <Backdrop color="secondary" open={!showSpinner.isIframeLoaded} className={styles['sizing-tab-loader']}>
          <CircularProgress color="primary" />
        </Backdrop>

        <div className={styles['panel-set']}>{panels}</div>
      </div>
    </div>
  );
}
